import type { QueryFunction } from '@tanstack/query-core';
import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { analysis, type RangeDebugResponse } from 'venn-api';
import { createRangeDebugKey, type RangeDebugKey } from './query-keys';
import type AnalysisSubject from '../analysis/AnalysisSubject';

type QueryData = RangeDebugResponse;
type QueryOptions = Pick<UseQueryOptions, 'suspense' | 'enabled'>;

export const useRangeDebugQuery = (
  subject: AnalysisSubject | undefined,
  options: QueryOptions = {},
  analysisViewId?: string,
): UseQueryResult<QueryData> => {
  return useQuery<QueryData, unknown, QueryData, RangeDebugKey>(
    createRangeDebugKey(subject, analysisViewId),
    rangeDebugQueryFn,
    options,
  );
};

export const rangeDebugQueryFn: QueryFunction<QueryData, RangeDebugKey> = async ({ queryKey }) => {
  const [_, subject, analysisViewId] = queryKey;
  if (!subject || subject.private) {
    return Promise.reject(new Error('Range debug is only available for historical portfolios'));
  }

  const response = await analysis({
    analyses: [
      {
        analysisType: 'RANGE_DEBUG',
      },
    ],
    subjects: [
      {
        comparisonType: 'PRIMARY',
        id: subject.id.toString(),
        isPrivate: false,
        primary: true,
        subjectType: 'PORTFOLIO',
        proxySetId: analysisViewId ?? null,
      },
    ],
  });

  const result = response.content.analyses?.[0]?.rangeDebug?.[0];
  if (!result) {
    return Promise.reject(new Error('Range debug analysis not found'));
  }
  return result;
};
